<template>
  <fields-list
    :fields="fields"
    :editing-data="editingData"
    @update-field="updateField($event.value, $event.field)"
  />
</template>


<script>
import { listsMixin } from "@/mixins/listsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import FieldsList from "@/newComponents/StockManagement/FieldsList.vue";
import { unitConverterMixin } from "@/mixins/management/unitConverterMixin";

export default {
  name: "ClosureFamilySpecificFields",

  components: {
    FieldsList,
  },
  mixins: [listsMixin, objectManagementMixin, unitConverterMixin],

  props: {
    /**
     * The specific fields values passed by the parent component
     */
    familySpecificFields: {
      type: Object,
      required: true
    },

    /**
     * Different types of installation options
     */
    installationTypeOptions: {
      type: Array,
      required: true
    },

    /**
     * Loss value of the closure
     */
    lossValue: {},

    editingData: {
      type: Boolean,
      required: true,
    }
  },

  watch: {
    editingData() {
      this.requiredFieldsFilled();
    }
  },

  computed: {
    fields() {
      return [
        {
          label: this.$i18n.tc("Closure", 1) + ' ' + this.$i18n.tc("Type", 1),
          value: !this.editingData
            ? this.parseSelectValue([this.familySpecificFields.smcl_closure_type], this.closureTypeOptions)
            : this.familySpecificFields.smcl_closure_type,
          varName: 'smcl_closure_type',
          inputType: 'select',
          disabled: false,
          required: true,
          options: this.closureTypeOptions,
          clearable: true,
          size: '100%'
        },
        {
          label: this.$i18n.t("InstallationType"),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.installation_type, this.installationTypeOptions)
            : this.familySpecificFields.installation_type,
          varName: 'installation_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.installationTypeOptions,
          clearable: true,
          size: '100%'
        },
        {
          label: this.$i18n.t("MaxHP"),
          value: this.familySpecificFields.smcl_max_hps,
          varName: 'smcl_max_hps',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("NrTrays"),
          value: this.familySpecificFields.smcl_nr_trays,
          varName: 'smcl_nr_trays',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("TrayCapacity"),
          value: this.familySpecificFields.smcl_tray_capacity,
          varName: 'smcl_tray_capacity',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxSplitters"),
          value: this.familySpecificFields.smcl_max_splitters,
          varName: 'smcl_max_splitters',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxSplices"),
          value: this.familySpecificFields.smcl_max_splices,
          varName: 'smcl_max_splices',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("LossValue"),
          value: this.lossValue,
          varName: 'sm_loss_value',
          inputType: 'number',
          disabled: false,
          step: '.00001',
          min: '0',
          size: '100%'
        },
        {
          label: this.$i18n.t("CableStubLength") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smcl_cable_stub_length,
          varName: 'smcl_cable_stub_length',
          inputType: 'number',
          disabled: false,
          step: '0.01',
          min: '0',
        },
        {
          label: this.$i18n.t("MaxCablesIn"),
          value: this.familySpecificFields.smcl_max_cables_in,
          varName: 'smcl_max_cables_in',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxCablesOut"),
          value: this.familySpecificFields.smcl_max_cables_out,
          varName: 'smcl_max_cables_out',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxDistributionCables"),
          value: this.familySpecificFields.smcl_max_dist_cables,
          varName: 'smcl_max_dist_cables',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("MaxDropCables"),
          value: this.familySpecificFields.smcl_max_drop_cables,
          varName: 'smcl_max_drop_cables',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '0',
        },
        {
          label: this.$i18n.t("Length") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smcl_length,
          varName: 'smcl_length',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.t("Width") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smcl_width,
          varName: 'smcl_width',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
        {
          label: this.$i18n.t("Height") + this.getUnitLabel("m"),
          value: this.familySpecificFields.smcl_height,
          varName: 'smcl_height',
          inputType: 'number',
          disabled: false,
          min: '0',
        },
      ]
    },

    /**
     * Loads the closure types
     *
     * @return {Array} - the array with the various closure types
     */
    closureTypeOptions() {
      return this.$store.getters.optionLists_closureLogicalType;
    }
  },

  methods: {
    /**
     * Validations for the various required fields
     *
     * @return {boolean} indicating if the fields are valid
     */
    requiredFieldsValidation() {
      return (
        !!this.familySpecificFields.installation_type &&
        this.familySpecificFields.installation_type.length > 0 &&
        !!this.familySpecificFields.smcl_closure_type &&
        (parseFloat(this.familySpecificFields.smcl_max_hps)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_max_splices)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_max_splitters)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_nr_trays)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_tray_capacity)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_max_drop_cables)) >= 0 &&
        (parseFloat(this.familySpecificFields.smcl_max_dist_cables)) >= 0
      );
    },
  }
};
</script>

<style scoped>
.text-in-between {
  margin: 0 10px;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/StockManagement/SpecificMaterialSections/MaterialFieldsClosure.vue